<template>
  <main
    v-if="storeTheme.files.subdomain === subdomain"
    id="card-theme"
    class="p-6 flex flex-col gap-6 w-full md:px-14"
  >
    <div class="bg-white p-6 rounded-lg shadow-md">
      <Loader v-if="loading" :loader="loading" loaderSize="h-96" />

      <div v-else id="card-theme-content" class="flex flex-col gap-6">
        <header class="flex justify-between items-center">
          <h2 class="text-xl font-semibold">
            {{ $t("theme.org.title") }} - {{ $t("theme.org.subdomain") }}
            <span class="text-gray-500">
              ({{ storeTheme.files.subdomain ?? subdomain }})
            </span>
          </h2>
          <Button
            v-if="storeTheme.files.subdomain"
            :label="$t('theme.org.action')"
            icon="pi pi-pencil"
            class="btn-primary"
            @click.prevent="goTo"
          />
        </header>

        <section id="logo-preview" class="flex flex-col gap-3">
          <h3 class="text-lg font-medium">{{ $t("theme.org.logo") }}</h3>
          <div
            class="flex justify-center items-center h-24 bg-gray-100 rounded-md"
          >
            <Logo v-if="logo.length === 0" />
            <img
              v-else
              :src="getFileUrl(logo[0]?.image!)"
              alt="logo"
              class="h-20 object-contain"
              loading="lazy"
            />
          </div>
        </section>

        <div id="partners-preview" class="mb-5">
          <p class="font-bold text-lg mb-2">{{ $t("theme.org.partners") }}</p>

          <div
            v-if="partners.length === 0"
            class="flex flex-wrap justify-center items-center min-h-[150px] bg-gray-100 rounded-md gap-5"
          >
            <img
              v-for="(img, index) in images"
              :key="index"
              :src="img"
              :alt="img"
              class="w-fit h-8 invert"
              loading="lazy"
            />
          </div>

          <div
            v-else
            class="flex flex-wrap justify-center items-center min-h-[150px] bg-gray-100 rounded-md gap-5"
          >
            <img
              v-for="(src, index) in partners"
              id="partners-imgs"
              :key="index"
              :src="getFileUrl(src.image!)"
              class="mx-2 w-fit h-10 invert"
              loading="lazy"
            />
          </div>
        </div>

        <section class="flex flex-col gap-3">
          <h3 class="text-lg font-medium">{{ $t("theme.org.primary") }}</h3>
          <PreviewColors :color="colors?.primary" />
        </section>

        <section class="flex flex-col gap-3">
          <h3 class="text-lg font-medium">{{ $t("theme.org.secondary") }}</h3>
          <PreviewColors :color="colors?.secondary" />
        </section>
      </div>
    </div>
  </main>
</template>

<script lang="ts">
import { useThemeStore } from "@/store/theme";
import { getFileUrl } from "@/utils/generics.util";
import { getUserFromStorage } from "@/utils/user-store.util";
import { computed, defineComponent, onMounted, ref } from "vue";
import { useRouter } from "vue-router";
import PreviewColors from "./preview-colors.vue";

export default defineComponent({
  name: "ThemeArea",
  components: { PreviewColors },
  setup() {
    const storeTheme = useThemeStore;
    const router = useRouter();
    const loading = ref(true);
    const subdomain = window.location.hostname.split(".")[0];
    const colors = computed(() => ({
      primary: storeTheme.files.theme?.colors["h-primary"],
      secondary: storeTheme.files.theme?.colors["h-secondary"],
    }));

    function goTo(): void {
      if (storeTheme.files?.images?.length !== 0) {
        router.push({
          name: "theme",
          params: { id: getUserFromStorage().id },
        });
      } else router.push({ name: "theme" });
    }

    const requireImages: any = require.context(
      "../../../assets/images/partners",
      false,
      /\.(png|jpe?g|svg)$/
    );

    const logo = computed(() =>
      storeTheme.files.images.filter((e) => e.type === "LOGO")
    );

    const partners = computed(() =>
      storeTheme.files.images.filter((e) => e.type === "PARTNERS")
    );

    const images = requireImages.keys().map(requireImages);

    onMounted(
      async () =>
        await storeTheme.getFiles().finally(() => (loading.value = false))
    );

    return {
      storeTheme,
      router,
      colors,
      images,
      logo,
      partners,
      loading,
      subdomain,
      getFileUrl,
      goTo,
    };
  },
});
</script>

<style lang="scss">
#card-theme {
  max-width: 100%;
  margin: 0 auto;
}

img {
  max-width: 100%;
  height: auto;
}

.btn-primary {
  background-color: #4f46e5;
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 0.375rem;
  font-weight: 500;
  transition: background-color 0.3s;
}

.btn-primary:hover {
  background-color: #4338ca;
}
</style>
